import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Form, Container, Row, Col, Button } from 'react-bootstrap';
import PaginationComponent from '../Pagination/PaginationComponent';
import { getAllProducts } from '../../services/Product/productService';
import CreateProductModal from './CreateProductModal';
import EditProductModal from './EditProductModal';
import ProductFilter from '../Filters/ProductFilter';
import { getMeasureUnitLabel } from '../Constants/measureUnits';
import { FaEdit } from 'react-icons/fa';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const mapProducts = () => ({
  active: true,
});

const Product = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [persons, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filterParams, setFilterParams] = useState(() => mapProducts());
  const previousFilterParams = useRef({});

  // Recalcula permissões sempre que o objeto permissions mudar
  const canView = useMemo(() => hasPermission('productView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('productCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('productUpdate'), [hasPermission, permissions]);

  const fetchProducts = useCallback(async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
    if (!canView) return;
    try {
      const data = await getAllProducts(filters, currentPage - 1, currentPageSize);
      setProducts(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setNumberOfElements(data.numberOfElements);
    } catch (error) {}
  }, [filterParams, page, pageSize, canView]);

  useEffect(() => {
    if (canView) {
      fetchProducts();
    }
  }, [page, pageSize, fetchProducts, canView]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleOpenCreateProductModal = () => {
    if (!checkPermission('productCreate')) {
      return;
    }
    setShowCreateProductModal(true);
  };
  const handleCloseCreateProductModal = () => setShowCreateProductModal(false);

  const handleOpenEditProductModal = (product) => {
    if (!checkPermission('productUpdate')) {
      return;
    }
    setSelectedProduct(product);
    setShowEditProductModal(true);
  };
  
  const handleCloseEditProductModal = () => {
    setSelectedProduct(null);
    setShowEditProductModal(false);
  };
  
  const handleFilter = (filters) => {
    if (!canView) return;
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      fetchProducts(filters, 1, pageSize);
      previousFilterParams.current = filters;
    }
  };

  const handleClearFilter = () => {
    if (!canView) return;
    setFilterParams({});
    setPage(1);
    fetchProducts({}, 1, pageSize);
    previousFilterParams.current = {};
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Produtos</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Produtos</h3>
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs={12} md="auto" className="d-flex align-items-center justify-content-md-end justify-content-center">
          <Form.Label className="me-2 mb-0">Mostrar</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
            className="me-2"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Col>
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
            <Button variant="primary" className="button-custom" onClick={handleOpenCreateProductModal}>
              Cadastrar Produto
            </Button>
          )}
        </Col>
      </Row>
      {canView && <ProductFilter onFilter={handleFilter} onClear={handleClearFilter} />}
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Unidade</th>
              <th>Peso Unidade (kg)</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {persons.map((product) => (
              <tr key={product.id}>
                <td>{product.oldId + ' - ' + product.description}</td>
                <td>{getMeasureUnitLabel(product.unit)}</td>
                <td>{product.unitWeight.toLocaleString('pt-BR')}</td>
                <td style={{textAlign: 'center'}}>
                  {canUpdate && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenEditProductModal(product)}
                      className="p-0"
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FaEdit className='icon-custom'/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={page}
        onPageChange={handlePageChange}
        totalElements={totalElements}
        size={pageSize}
        pageNumber={page - 1}
        numberOfElements={numberOfElements}
      />

      <CreateProductModal
        show={showCreateProductModal}
        handleClose={handleCloseCreateProductModal}
        refreshProducts={fetchProducts}
      />

      <EditProductModal
        show={showEditProductModal}
        handleClose={handleCloseEditProductModal}
        product={selectedProduct}
        refreshProducts={fetchProducts}
      />
    </Container>
  );
};

export default Product;